import React, { useCallback, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { AI_AUDIO_BRIEFING } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import { useAudioBriefing } from '../../../hooks/useAudioBriefing';
import { getSharedAttributes } from '../../../utils/getSharedAttributes';
import {
	getAudioLengthMs,
	getAudioCurrentTimeMs,
	getAudioProgress,
} from '../../../utils/audioTimeUtils';
import { AI_AUDIO_BRIEFING_INTERACTION_METRIC } from '../perf.config';

const i18n = defineMessages({
	buttonText: {
		id: 'audio.audio-popup.restart-briefing-button',
		defaultMessage: 'Re-start briefing',
		description: 'Text in a button that will regenerate and play an audio briefing when clicked',
	},
});

export type RestartBriefingButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	source: 'customizePanel' | 'sourcesPanel';
};

export const RestartBriefingButton = ({ audioRef, source }: RestartBriefingButtonProps) => {
	const [
		{
			streamEnded,
			activeContent,
			customization,
			additionalResources,
			playbackSpeed,
			hasFetched,
			lastGeneratedBriefingConfig,
			hasEndBeenReached,
			isLoading,
			activeFeature,
		},
		{ fetchBriefing },
	] = useAudioBriefing();

	const { locale, formatMessage } = useIntl();

	const { trackAIInteractionInit, trackAIResultAction } = useAIEventsInstrumentation();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticsUIEvent = useCallback(
		(actionSubjectId: string, additionalAttributes?: Record<string, number | string>) => {
			const attributes = {
				audioLocale: locale,
				length: customization.length,
				tone: customization.tone,
				sourceCount: additionalResources.length,
				hasFetched,
				feature: activeFeature,
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			locale,
			hasFetched,
			customization,
			additionalResources,
			source,
			activeFeature,
		],
	);

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}

		const abortReason =
			source === 'customizePanel'
				? 'regenerated briefing from the customize panel'
				: 'regenerated briefing from the sources panel';

		if (!streamEnded) {
			experienceTracker.abort({
				name: AI_AUDIO_BRIEFING,
				reason: abortReason,
			});
		}

		const audioClosedAttributes = {
			audioLengthMs: getAudioLengthMs(audioRef),
			audioCurrentTimeMs: getAudioCurrentTimeMs(audioRef),
			percentCompleted: getAudioProgress(audioRef),
			reason: abortReason,
		};

		trackAIResultAction('audioClosed', {
			attributes: {
				...getSharedAttributes({
					customization: lastGeneratedBriefingConfig?.customization,
					additionalResources: lastGeneratedBriefingConfig?.additionalResources,
					playbackSpeed,
					hasFetched,
					activeFeature,
				}),
				...audioClosedAttributes,
				hasEndBeenReached,
			},
		});
	}, [
		audioRef,
		experienceTracker,
		streamEnded,
		source,
		trackAIResultAction,
		lastGeneratedBriefingConfig,
		playbackSpeed,
		hasFetched,
		hasEndBeenReached,
		activeFeature,
	]);

	const startNewAudio = useCallback(() => {
		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});

		AI_AUDIO_BRIEFING_INTERACTION_METRIC.start();
		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature,
			}),
		});

		sendAnalyticsUIEvent('audioRegenerate');
		fetchBriefing();
	}, [
		experienceTracker,
		trackAIInteractionInit,
		sendAnalyticsUIEvent,
		fetchBriefing,
		activeContent,
		customization,
		additionalResources,
		playbackSpeed,
		hasFetched,
		activeFeature,
	]);

	const handleClick = useCallback(() => {
		stopExistingAudio();
		startNewAudio();
	}, [stopExistingAudio, startNewAudio]);

	return (
		<Button
			testId="restart-briefing-button"
			shouldFitContainer
			appearance="primary"
			onClick={handleClick}
			isLoading={isLoading}
		>
			{formatMessage(i18n.buttonText)}
		</Button>
	);
};
